import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageBanner from "@/components/PageBanner";
import SectionTitle from "@/components/SectionTitle";
import { COMPANY, IMAGES } from "@/lib/content";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Logistics",
  "Global Trade",
  "Clearance",
  "Car Sales",
  "Car Rental",
  "General Inquiry",
];

const Contact = () => {
  const [form, setForm] = useState({
    full_name: "",
    company: "",
    phone: "",
    email: "",
    service_needed: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.service_needed || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Your inquiry has been received. Our team will be in touch shortly.");
      setForm({
        full_name: "", company: "", phone: "", email: "", service_needed: "", message: "",
      });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || "Unable to submit right now. Please try again.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const contactCards = [
    { icon: Phone, title: "Call Us", lines: COMPANY.phones, hrefs: COMPANY.phones.map((p) => `tel:${p.replace(/\s+/g, "")}`) },
    { icon: Mail, title: "Email Us", lines: [COMPANY.email], hrefs: [`mailto:${COMPANY.email}`] },
    { icon: MapPin, title: "Visit Us", lines: [COMPANY.address], hrefs: [null] },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-abayle-ivory">
      <Navbar />
      <main>
        <PageBanner
          eyebrow="Contact Us"
          title="Contact Us"
          subtitle="Get in touch with Abayle Logistics and Trading for reliable support across all five service lines."
          image={IMAGES.aboutPort}
        />

        {/* Contact Info Cards */}
        <section className="bg-white" data-testid="contact-info">
          <div className="container-abayle py-14 sm:py-20 lg:py-24">
            <SectionTitle
              centered
              eyebrow="Get in Touch"
              title="Speak With Our Team"
              subtitle="Whether you need support with logistics, global trade, clearance, car sales, or car rental, our team is ready to respond."
            />
            <div className="grid md:grid-cols-3 gap-6">
              {contactCards.map((c, idx) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="card-abayle text-center" data-testid={`contact-info-${idx}`}>
                    <div className="mx-auto h-16 w-16 rounded-full bg-abayle-green flex items-center justify-center border-2 border-abayle-gold mb-5">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-abayle-green font-bold text-lg mb-3">{c.title}</h3>
                    <div className="space-y-1.5">
                      {c.lines.map((line, i) => (
                        <div key={line + i}>
                          {c.hrefs[i] ? (
                            <a href={c.hrefs[i]} className="text-abayle-charcoal/85 hover:text-abayle-gold transition-colors text-sm">
                              {line}
                            </a>
                          ) : (
                            <span className="text-abayle-charcoal/85 text-sm">{line}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Form + Map */}
        <section className="bg-abayle-ivory" data-testid="contact-form-section">
          <div className="container-abayle py-14 sm:py-20 lg:py-28 grid lg:grid-cols-5 gap-8 sm:gap-10 lg:gap-16">
            <div className="lg:col-span-3 bg-white border border-abayle-gray-border rounded-lg p-8 lg:p-10 shadow-sm">
              <SectionTitle
                eyebrow="Send an Inquiry"
                title="Tell Us About Your Requirements"
              />
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="contact-form">
                <div className="sm:col-span-1">
                  <label className="block text-sm font-semibold text-abayle-charcoal mb-2">Full Name *</label>
                  <input
                    type="text"
                    className="input-abayle"
                    value={form.full_name}
                    onChange={update("full_name")}
                    required
                    data-testid="contact-input-full_name"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-sm font-semibold text-abayle-charcoal mb-2">Company / Organisation</label>
                  <input
                    type="text"
                    className="input-abayle"
                    value={form.company}
                    onChange={update("company")}
                    data-testid="contact-input-company"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-sm font-semibold text-abayle-charcoal mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    className="input-abayle"
                    value={form.phone}
                    onChange={update("phone")}
                    required
                    data-testid="contact-input-phone"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-sm font-semibold text-abayle-charcoal mb-2">Email Address *</label>
                  <input
                    type="email"
                    className="input-abayle"
                    value={form.email}
                    onChange={update("email")}
                    required
                    data-testid="contact-input-email"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-abayle-charcoal mb-2">Service Needed *</label>
                  <Select
                    value={form.service_needed}
                    onValueChange={(v) => setForm((f) => ({ ...f, service_needed: v }))}
                  >
                    <SelectTrigger
                      className="w-full h-12 bg-white border-abayle-gray-border text-abayle-charcoal focus:border-abayle-green focus:ring-1 focus:ring-abayle-green"
                      data-testid="contact-select-service"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} data-testid={`service-option-${opt.toLowerCase().replace(/\s+/g, "-")}`}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-abayle-charcoal mb-2">Message *</label>
                  <textarea
                    rows={5}
                    className="input-abayle resize-none"
                    value={form.message}
                    onChange={update("message")}
                    required
                    data-testid="contact-input-message"
                  />
                </div>
                <div className="sm:col-span-2 mt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary-abayle disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send Inquiry
                        <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            <aside className="lg:col-span-2 flex flex-col gap-6" data-testid="contact-aside">
              <div className="bg-abayle-green-dark text-white rounded-lg p-8 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 80% 10%, #C9A13B 0, transparent 40%)" }} />
                <div className="relative">
                  <div className="eyebrow mb-4">Our Location</div>
                  <h3 className="text-2xl font-bold mb-4">Based in Mogadishu</h3>
                  <p className="text-white/85 leading-relaxed mb-6">
                    Abayle Logistics and Trading is based in Mogadishu and positioned to support clients operating
                    across Somalia.
                  </p>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-abayle-gold mt-0.5 shrink-0" />
                      <span>{COMPANY.address}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Phone className="h-4 w-4 text-abayle-gold mt-0.5 shrink-0" />
                      <span>{COMPANY.phones.join("  •  ")}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="h-4 w-4 text-abayle-gold mt-0.5 shrink-0" />
                      <span>{COMPANY.email}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg overflow-hidden border border-abayle-gray-border shadow-sm" data-testid="contact-map">
                <iframe
                  title="Abayle Location"
                  src="https://www.google.com/maps?q=Aden+Adde+International+Airport+Mogadishu&output=embed"
                  className="w-full h-[300px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
